import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Download,
  FileSpreadsheet,
  IdCard,
  Info,
  LockKeyhole,
  Mail,
  Send,
  ShieldCheck,
  Trash2,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import API from "../../services/api";
import "./HREmployeeCredentialsPage.css";

const initialCredentials = {
  personalMail: "",
  employeeName: "",
  employeeEmail: "",
  employeeId: "",
  temporaryPassword: "",
};

const credentialsRecordsStorageKey = "hrEmployeeCredentialRecords";

const escapeCsvValue = (value) => {
  const normalizedValue = value === null || value === undefined ? "" : String(value);
  return `"${normalizedValue.replace(/"/g, '""')}"`;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateCredentials = (values) => {
  const errors = {};
  const personalMail = values.personalMail.trim();
  const employeeName = values.employeeName.trim();
  const employeeEmail = values.employeeEmail.trim();
  const employeeId = values.employeeId.trim();
  const temporaryPassword = values.temporaryPassword.trim();

  if (!personalMail) {
    errors.personalMail = "Personal mail is required.";
  } else if (!emailPattern.test(personalMail)) {
    errors.personalMail = "Enter a valid personal mail address.";
  }

  if (!employeeName) {
    errors.employeeName = "Employee name is required.";
  }

  if (!employeeEmail) {
    errors.employeeEmail = "Employee email is required.";
  } else if (!emailPattern.test(employeeEmail)) {
    errors.employeeEmail = "Enter a valid employee email address.";
  }

  if (!employeeId) {
    errors.employeeId = "Employee ID is required.";
  }

  if (!temporaryPassword) {
    errors.temporaryPassword = "Temporary password is required.";
  }

  return errors;
};

const createCredentialRecord = (credentials, status = "Sent") => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  employeeName: credentials.employeeName.trim(),
  personalMail: credentials.personalMail.trim(),
  employeeEmail: credentials.employeeEmail.trim(),
  employeeId: credentials.employeeId.trim(),
  temporaryPassword: credentials.temporaryPassword.trim(),
  status,
  sentOn: new Date(),
});

const formatCredentialStatus = (status) => {
  const normalizedStatus = String(status || "SENT").toLowerCase();
  return normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1);
};

const normalizeCredentialRecord = (record) => ({
  id: record.id,
  employeeName: record.employeeName || "",
  personalMail: record.personalMail || "",
  employeeEmail: record.employeeEmail || "",
  employeeId: record.employeeId || "",
  temporaryPassword: record.temporaryPassword || "",
  status: formatCredentialStatus(record.emailStatus),
  sentOn: new Date(record.emailSentAt || record.createdAt || Date.now()),
});

const HREmployeeCredentialsPage = () => {
  const [credentials, setCredentials] = useState(initialCredentials);
  const [fieldErrors, setFieldErrors] = useState({});
  const [popup, setPopup] = useState({ open: false, type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingRecords, setIsFetchingRecords] = useState(false);
  const [deletingRecordIds, setDeletingRecordIds] = useState([]);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    localStorage.removeItem(credentialsRecordsStorageKey);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchCredentialRecords = async () => {
      setIsFetchingRecords(true);

      try {
        const response = await API.get("/hr/employee-credentials");
        const fetchedRecords = Array.isArray(response.data?.data)
          ? response.data.data.map(normalizeCredentialRecord)
          : [];

        if (isMounted) {
          setRecords(fetchedRecords);
        }
      } catch (error) {
        if (isMounted) {
          setPopup({
            open: true,
            type: "error",
            message:
              error.response?.data?.message ||
              "Failed to fetch employee credential records.",
          });
        }
      } finally {
        if (isMounted) {
          setIsFetchingRecords(false);
        }
      }
    };

    fetchCredentialRecords();

    return () => {
      isMounted = false;
    };
  }, []);

  const canSubmit = useMemo(() => {
    return (
      credentials.personalMail.trim() &&
      credentials.employeeName.trim() &&
      credentials.employeeEmail.trim() &&
      credentials.employeeId.trim() &&
      credentials.temporaryPassword.trim()
    );
  }, [credentials]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setCredentials((current) => ({
      ...current,
      [name]: value,
    }));
    setFieldErrors((current) => ({
      ...current,
      [name]: "",
    }));
    setPopup({ open: false, type: "", message: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validateCredentials(credentials);

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setPopup({
        open: true,
        type: "error",
        message: "Please correct the highlighted fields.",
      });
      return;
    }

    if (!canSubmit) {
      setPopup({
        open: true,
        type: "error",
        message: "Please fill all required employee credential fields.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await API.post("/hr/employee-credentials/send", credentials);
      const message =
        response.data?.message ||
        "Credentials sent to personal mail successfully.";

      setCredentials(initialCredentials);
      setFieldErrors({});
      setRecords((current) => {
        const savedRecord = response.data?.data
          ? normalizeCredentialRecord(response.data.data)
          : createCredentialRecord(credentials, "Sent");

        return [savedRecord, ...current];
      });
      setPopup({
        open: true,
        type: "success",
        message,
      });
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to send credentials. Please try again.";

      setPopup({
        open: true,
        type: "error",
        message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!recordId || deletingRecordIds.includes(recordId)) {
      return;
    }

    setDeletingRecordIds((current) => [...current, recordId]);

    try {
      const response = await API.delete(`/hr/employee-credentials/${recordId}`);

      setRecords((current) => current.filter((record) => record.id !== recordId));
      setPopup({
        open: true,
        type: "success",
        message:
          response.data?.message ||
          "Employee credential deleted successfully.",
      });
    } catch (error) {
      setPopup({
        open: true,
        type: "error",
        message:
          error.response?.data?.message ||
          "Failed to delete employee credential.",
      });
    } finally {
      setDeletingRecordIds((current) => current.filter((id) => id !== recordId));
    }
  };

  const downloadCsv = (selectedRecords, fileName) => {
    const headers = [
      "Employee Name",
      "Personal Mail",
      "Employee Email",
      "Employee ID",
      "Temporary Password",
      "Status",
      "Sent On",
    ];
    const rows = selectedRecords.map((record) => [
      record.employeeName,
      record.personalMail,
      record.employeeEmail,
      record.employeeId,
      record.temporaryPassword,
      record.status,
      record.sentOn.toLocaleString(),
    ]);
    const csv = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleDownloadRecord = (record) => {
    downloadCsv([record], `${record.employeeId || "employee"}-credentials.csv`);
  };

  const handleDownloadExcel = () => {
    downloadCsv(records, "employee-credentials.xls");
  };

  return (
    <section className="employee-credentials-page">
      {popup.open ? (
        <div
          className="credentials-popup-overlay"
          role="presentation"
          onClick={() => setPopup({ open: false, type: "", message: "" })}
        >
          <div
            className={`credentials-popup ${popup.type}`}
            role="dialog"
            aria-modal="true"
            aria-label="Credentials status"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="credentials-popup-close"
              aria-label="Close popup"
              onClick={() => setPopup({ open: false, type: "", message: "" })}
            >
              <X size={18} />
            </button>
            <div className="credentials-popup-icon">
              {popup.type === "success" ? <CheckCircle2 size={24} /> : <ShieldCheck size={24} />}
            </div>
            <p>{popup.message}</p>
          </div>
        </div>
      ) : null}

      <article className="credentials-card">
        <div className="credentials-card-title">
          <span className="credentials-card-icon">
            <UsersRound size={23} />
          </span>
          <h2>Employee Credentials Information</h2>
        </div>

        <form className="credentials-form" onSubmit={handleSubmit} noValidate>
          <div className="credentials-form-columns">
            <div className="credentials-form-column">
              <label className="credentials-field">
                <span>
                  Personal Mail <strong>*</strong>
                </span>
                <div className={`credentials-input-wrap ${fieldErrors.personalMail ? "has-error" : ""}`}>
                  <span className="credentials-input-icon">
                    <Mail size={19} />
                  </span>
                  <input
                    type="email"
                    name="personalMail"
                    value={credentials.personalMail}
                    onChange={handleChange}
                    placeholder="Enter personal email address"
                    required
                    aria-invalid={Boolean(fieldErrors.personalMail)}
                    aria-describedby={fieldErrors.personalMail ? "personal-mail-error" : undefined}
                  />
                </div>
                {fieldErrors.personalMail ? (
                  <span className="credentials-error" id="personal-mail-error">
                    {fieldErrors.personalMail}
                  </span>
                ) : null}
              </label>

              <label className="credentials-field">
                <span>
                  Employee Name <strong>*</strong>
                </span>
                <div className={`credentials-input-wrap ${fieldErrors.employeeName ? "has-error" : ""}`}>
                  <span className="credentials-input-icon">
                    <UserRound size={19} />
                  </span>
                  <input
                    type="text"
                    name="employeeName"
                    value={credentials.employeeName}
                    onChange={handleChange}
                    placeholder="Enter employee full name"
                    required
                    aria-invalid={Boolean(fieldErrors.employeeName)}
                    aria-describedby={fieldErrors.employeeName ? "employee-name-error" : undefined}
                  />
                </div>
                {fieldErrors.employeeName ? (
                  <span className="credentials-error" id="employee-name-error">
                    {fieldErrors.employeeName}
                  </span>
                ) : null}
              </label>

              <label className="credentials-field">
                <span>
                  Employee Email <strong>*</strong>
                </span>
                <div className={`credentials-input-wrap ${fieldErrors.employeeEmail ? "has-error" : ""}`}>
                  <span className="credentials-input-icon">
                    <Mail size={19} />
                  </span>
                  <input
                    type="email"
                    name="employeeEmail"
                    value={credentials.employeeEmail}
                    onChange={handleChange}
                    placeholder="Enter employee email address"
                    required
                    aria-invalid={Boolean(fieldErrors.employeeEmail)}
                    aria-describedby={fieldErrors.employeeEmail ? "employee-email-error" : undefined}
                  />
                </div>
                {fieldErrors.employeeEmail ? (
                  <span className="credentials-error" id="employee-email-error">
                    {fieldErrors.employeeEmail}
                  </span>
                ) : null}
              </label>
            </div>

            <div className="credentials-form-column">
              <label className="credentials-field">
                <span>
                  Employee ID <strong>*</strong>
                </span>
                <div className={`credentials-input-wrap ${fieldErrors.employeeId ? "has-error" : ""}`}>
                  <span className="credentials-input-icon">
                    <IdCard size={19} />
                  </span>
                  <input
                    type="text"
                    name="employeeId"
                    value={credentials.employeeId}
                    onChange={handleChange}
                    placeholder="Enter employee ID"
                    required
                    aria-invalid={Boolean(fieldErrors.employeeId)}
                    aria-describedby={fieldErrors.employeeId ? "employee-id-error" : undefined}
                  />
                </div>
                {fieldErrors.employeeId ? (
                  <span className="credentials-error" id="employee-id-error">
                    {fieldErrors.employeeId}
                  </span>
                ) : null}
              </label>

              <div className="credentials-field">
                <span>
                  Temporary Password <strong>*</strong>
                </span>
                <div className={`credentials-input-wrap password-input ${fieldErrors.temporaryPassword ? "has-error" : ""}`}>
                  <span className="credentials-input-icon">
                    <LockKeyhole size={19} />
                  </span>
                  <input
                    type="password"
                    name="temporaryPassword"
                    value={credentials.temporaryPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                    placeholder="Enter temporary password"
                    required
                    aria-invalid={Boolean(fieldErrors.temporaryPassword)}
                    aria-describedby={fieldErrors.temporaryPassword ? "temporary-password-error" : undefined}
                  />
                </div>
                {fieldErrors.temporaryPassword ? (
                  <span className="credentials-error" id="temporary-password-error">
                    {fieldErrors.temporaryPassword}
                  </span>
                ) : null}
              </div>

              <button type="submit" className="credentials-submit-btn" disabled={isSubmitting}>
                <Send size={22} />
                <span>{isSubmitting ? "Sending..." : "Send Credentials"}</span>
              </button>
            </div>
          </div>

        </form>

        <div className="credentials-helper">
          <Info size={18} />
          <span>
            The employee will receive an email with their login credentials.
          </span>
        </div>
      </article>

      <article className="credentials-records-card">
        <div className="credentials-records-header">
          <h3>Sent Credentials Records</h3>
          <div className="credentials-record-actions">
            <button
              type="button"
              className="credentials-download-excel"
              onClick={handleDownloadExcel}
              disabled={!records.length}
            >
              <FileSpreadsheet size={17} />
              <span>Download Excel</span>
            </button>
          </div>
        </div>

        <div className="credentials-table-wrap">
          <div className="credentials-table-head">
            <span>#</span>
            <span>Employee Name</span>
            <span>Personal Mail</span>
            <span>Employee Email</span>
            <span>Employee ID</span>
            <span>Temporary Password</span>
            <span>Status</span>
            <span>Sent On</span>
            <span>Action</span>
          </div>
          {isFetchingRecords ? (
            <div className="credentials-records-empty">Loading credential records...</div>
          ) : records.length ? (
            records.map((record, index) => (
              <div className="credentials-table-row" key={record.id}>
                <span data-label="#">{index + 1}</span>
                <span data-label="Employee Name">{record.employeeName}</span>
                <span data-label="Personal Mail">{record.personalMail}</span>
                <span data-label="Employee Email">{record.employeeEmail}</span>
                <span data-label="Employee ID">{record.employeeId}</span>
                <span data-label="Temporary Password">{record.temporaryPassword}</span>
                <span data-label="Status">
                  <mark className={`credentials-status ${record.status.toLowerCase()}`}>
                    {record.status}
                  </mark>
                </span>
                <span data-label="Sent On">{record.sentOn.toLocaleString()}</span>
                <span className="credentials-row-actions" data-label="Action">
                  <button type="button" aria-label="Download record" onClick={() => handleDownloadRecord(record)}>
                    <Download size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Delete record"
                    disabled={deletingRecordIds.includes(record.id)}
                    onClick={() => handleDeleteRecord(record.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </span>
              </div>
            ))
          ) : (
            <div className="credentials-records-empty">No sent credentials records yet.</div>
          )}
        </div>

        <div className="credentials-records-footer">
          <span>
            Showing {records.length ? 1 : 0} to {records.length} of {records.length} entries
          </span>
          <div className="credentials-pagination">
            <button type="button" disabled>Previous</button>
            <strong>1</strong>
            <button type="button" disabled>Next</button>
          </div>
        </div>

      </article>
    </section>
  );
};

export default HREmployeeCredentialsPage;
